import { useState } from "react"
import { useDispatch } from "react-redux"
import { useLazyFetchCoordsToPositionQuery, useLazyFetchPathPositionCoordsQuery } from "../../../../api/rtk/base/slices/mapApiSlice"
import { useLazyFetchGeoAddressQuery } from "../../../../api/rtk/geocoder/slices/geocoderApiSlice"
import { useLazyGetFeatureWFSQuery } from "../../../../api/rtk/mapWFS/slices/mapWFSApiSlice"
import { getItemWebStorage } from "../../../../features/webStorageSaver"
import {
    setHighlightObjectGeoJSON,
    setHighlightObjectStyleConfig,
    setMapPosition,
    showToastMessage
} from "../../../../stateManagement/reducers"
import { checkGeoJSONGeometryIsNotEmpty } from "../../../map/mapFeatures/helpers"
import { mapStylesColors } from "../../../map/mapFeatures/styles/mapStyles"
import { getClosestPointOnPathGeoJSON, normalizePathPositionInput as normalizePathPositionUtils } from "../utils/searchUtils"
import { fromLonLat } from "ol/proj"
import { containsCoordinate } from "ol/extent"
import { store } from "../../../../stateManagement/store"
import {
    clearSearchMapArtifacts,
    removeSearchConnectorFigure,
    removeSearchLocationOverlay,
    SEARCH_HIGHLIGHT_OWNER,
    SEARCH_LOCATION_MARKER_ID,
    SEARCH_LOCATION_OVERLAY_ID,
    SEARCH_OFFSET_CONNECTOR_ID,
    upsertSearchConnectorFigure,
    upsertSearchGeoMarker,
    upsertSearchLocationOverlay,
} from "./searchMapState"

export function useMapNavigation() {
    const dispatch = useDispatch()

    const [isPathSelected, setIsPathSelected] = useState<boolean>(false)
    const [selectedPathMeta, setSelectedPathMeta] = useState<any>(null)
    const [pathPositionInputValue, setPathPositionInputValue] = useState<any>("")
    const [pathPositionQuery, setPathPositionQuery] = useState<any>("")
    const [selectedPathMinPosition, setSelectedPathMinPosition] = useState<any>(null)
    const [selectedPathMaxEnding, setSelectedPathMaxEnding] = useState<any>(null)

    const [fetchWfsFeature, { isFetching: isFetchingWfs }] = useLazyGetFeatureWFSQuery()
    const [fetchPathPositionCoords, { isFetching: isFetchingPathPos }] = useLazyFetchPathPositionCoordsQuery()
    const [fetchCoordsToPosition] = useLazyFetchCoordsToPositionQuery()
    const [fetchGeoAddress] = useLazyFetchGeoAddressQuery()

    function setSelectedPathPositionRange(pathGeoJSON: any) {
        const minPosition = Math.min(...pathGeoJSON.features.map((feature: any) => feature.properties.Position))
        const maxEnding = Math.max(...pathGeoJSON.features.map((feature: any) => feature.properties.Ending))

        if (minPosition) setSelectedPathMinPosition(minPosition.toFixed(3))
        if (maxEnding) setSelectedPathMaxEnding(maxEnding.toFixed(3))
    }

    async function highlightPathById(
        pathId: any,
        opts?: { fit?: boolean; clearMarkersAndOverlays?: boolean; resetPathPositionUI?: boolean; updatePositionRange?: boolean }
    ) {
        removeSearchConnectorFigure()

        const { workspace } = getItemWebStorage("currentConfig")
        const cqlFilter = `PathID = ${pathId}`
        const { data: pathGeoJSON } = await fetchWfsFeature({ workspace, layer: "MALink", cqlFilter })

        if (!checkGeoJSONGeometryIsNotEmpty(pathGeoJSON)) {
            dispatch(showToastMessage({ messageText: "Объект содержит пустую геометрию", messageType: "error", windowType: "map" }))
            return { isSuccessful: false }
        }

        dispatch(setHighlightObjectGeoJSON(pathGeoJSON))
        dispatch(setHighlightObjectStyleConfig({ kind: "line", color: mapStylesColors?.mustard, width: 4, fit: opts?.fit, owner: SEARCH_HIGHLIGHT_OWNER }))

        if (opts?.updatePositionRange !== false) setSelectedPathPositionRange(pathGeoJSON)

        if (opts?.clearMarkersAndOverlays !== false) {
            clearSearchMapArtifacts()
        }

        if (opts?.resetPathPositionUI !== false) {
            setPathPositionInputValue("")
            setPathPositionQuery("")
        }

        return { isSuccessful: true, pathGeoJSON }
    }

    async function goToPath(pathId: any) {
        return await highlightPathById(pathId, { fit: true, clearMarkersAndOverlays: true, resetPathPositionUI: true, updatePositionRange: true })
    }

    function goToPosition(
        mapPosition: any,
        overlayMeta?: {
            road?: string
            path?: string
            maintenancePosition?: string
            keepHighlight?: boolean
            kmM?: string
            title?: string
        }
    ) {
        const resolveCurrentZoom = () => {
            const zFromState = store.getState()?.map?.mapPosition?.zoom
            if (typeof zFromState === "number" && Number.isFinite(zFromState)) return zFromState

            const zFromStorage = getItemWebStorage("mapPosition")?.zoom
            if (typeof zFromStorage === "number" && Number.isFinite(zFromStorage)) return zFromStorage

            const zFromConfig = getItemWebStorage("currentConfig")?.mapDefaults?.zoom
            if (typeof zFromConfig === "number" && Number.isFinite(zFromConfig)) return zFromConfig

            return undefined
        }

        const latitude = Number(mapPosition?.latitude)
        const longitude = Number(mapPosition?.longitude)

        const zoom =
            typeof mapPosition?.zoom === "number" && Number.isFinite(mapPosition.zoom)
                ? mapPosition.zoom
                : resolveCurrentZoom()

        dispatch(setMapPosition({ latitude, longitude, zoom }))
        upsertSearchGeoMarker({ id: SEARCH_LOCATION_MARKER_ID, latitude, longitude })

        const keepRoadHighlight =
            !!overlayMeta?.keepHighlight ||
            (!!overlayMeta?.road && overlayMeta?.kmM != null && `${overlayMeta.kmM}`.length > 0)

        if (!keepRoadHighlight) {
            const styleConfig = store.getState().map.highlightObjectStyleConfig
            if (styleConfig?.owner === SEARCH_HIGHLIGHT_OWNER) {
                dispatch(setHighlightObjectGeoJSON(null))
                dispatch(setHighlightObjectStyleConfig(null))
            }
        }

        if (Number.isFinite(latitude) && Number.isFinite(longitude)) {
            upsertSearchLocationOverlay({
                id: SEARCH_LOCATION_OVERLAY_ID,
                type: "locationInfo",
                position: { latitude, longitude },
                data: {
                    latitude,
                    longitude,
                    road: overlayMeta?.road,
                    path: overlayMeta?.path,
                    maintenancePosition: overlayMeta?.maintenancePosition,
                    kmM: overlayMeta?.kmM,
                    title: overlayMeta?.title
                }
            })
        } else {
            removeSearchLocationOverlay()
        }
    }

    async function goToSearchResult(
        searchResult: any, 
        localMapExtent: any,
        callbacks: { 
            setSearchInputValue: (val: string) => void,
            setIsResultsVisible: (val: boolean) => void,
            afterMouseClick?: boolean
        }
    ) {
        const { setSearchInputValue, setIsResultsVisible, afterMouseClick = true } = callbacks
        
        const isPathResult = !!searchResult?.PathID
        let displayName = ""

        if (isPathResult) {
            const { Name, RoadName, PathID, RoadId } = searchResult ?? {}
            displayName = `${RoadName ?? ""} ${Name ?? ""}`.trim()

            const { isSuccessful } = await goToPath(PathID)
            if (isSuccessful) {
                setSelectedPathMeta({ pathId: PathID, roadId: RoadId, roadName: RoadName, pathName: Name })
                setIsPathSelected(true)
                setSearchInputValue(displayName)

                if (afterMouseClick) setIsResultsVisible(false)
            }
            return
        }

        const { lat, lon, name } = searchResult ?? {}
        const coordinates = fromLonLat([lon, lat])

        if (!containsCoordinate(localMapExtent, coordinates)) {
            dispatch(
                showToastMessage({
                    messageText: "Данное местоположение находится вне зоны используемого сервера",
                    messageType: "error",
                    windowType: "map"
                })
            )
            return
        }

        displayName = name
        const newPosition = { latitude: lat, longitude: lon }

        removeSearchConnectorFigure()

        let overlayMeta: any = { title: displayName }

        try {
            const { data: pos }: any = await fetchCoordsToPosition({ latitude: lat, longitude: lon })
            if (pos && typeof pos === "object" && !Object.hasOwn(pos, "Error")) {
                const offset = Number(pos?.Offset)
                const isOffsetOk = Number.isFinite(offset) && Math.abs(offset) < 100

                if (isOffsetOk) {
                    const posPathId = pos?.PathId ?? pos?.PathID
                    const road = pos?.RoadName
                    const path = pos?.PathName
                    const maintenancePosition = pos?.MaintenancePosition
                    overlayMeta.title = "Результат поиска"

                    let highlightedGeoJSON: any = null
                    if (posPathId != null) {
                        const res: any = await highlightPathById(posPathId, {
                            fit: false,
                            clearMarkersAndOverlays: false,
                            resetPathPositionUI: false,
                            updatePositionRange: false
                        })
                        highlightedGeoJSON = res?.pathGeoJSON
                        overlayMeta.keepHighlight = true
                    }

                    const isOffsetBig = Number.isFinite(offset) && Math.abs(offset) > 5
                    if (isOffsetBig && highlightedGeoJSON) {
                        const snapped = getClosestPointOnPathGeoJSON(highlightedGeoJSON, Number(lon), Number(lat))
                        if (snapped) {
                            upsertSearchConnectorFigure({
                                type: "line",
                                id: SEARCH_OFFSET_CONNECTOR_ID,
                                from: { longitude: Number(lon), latitude: Number(lat) },
                                to: { longitude: snapped.longitude, latitude: snapped.latitude },
                                style: {
                                    mode: "dashed",
                                    color: "rgba(0,0,0,0.7)",
                                    width: 2,
                                    label: {
                                        text: `${Math.round(Math.abs(offset))} м`,
                                        repeat: 180,
                                        offsetY: -10,
                                    },
                                },
                                zIndex: 950
                            })
                        }
                    }

                    overlayMeta = {
                        ...overlayMeta,
                        ...(road ? { road: String(road) } : {}),
                        ...(path ? { path: String(path) } : {}),
                        ...(maintenancePosition != null ? { maintenancePosition: String(maintenancePosition) } : {})
                    }
                } else {
                    try {
                        const { data: address }: any = await fetchGeoAddress({
                            latitude: Number(lat),
                            longitude: Number(lon)
                        })
                        if (address) overlayMeta.title = String(address)
                    } catch { }
                }
            } else {
                try {
                    const { data: address }: any = await fetchGeoAddress({
                        latitude: Number(lat),
                        longitude: Number(lon)
                    })
                    if (address) overlayMeta.title = String(address)
                } catch { }
            }
        } catch {
            try {
                const { data: address }: any = await fetchGeoAddress({
                    latitude: Number(lat),
                    longitude: Number(lon)
                })
                if (address) overlayMeta.title = String(address)
            } catch { }
        }

        goToPosition(newPosition, overlayMeta)
        setIsPathSelected(false)
        setSelectedPathMeta(null)
        setSearchInputValue(displayName)

        if (afterMouseClick) setIsResultsVisible(false)
    }

    async function goToPositionOnSelectedPath(callbacks: { setSearchInputValue: (val: string) => void }) {
        const { setSearchInputValue } = callbacks
        const { pathId, roadId, roadName, pathName } = selectedPathMeta
        let coordsResponse

        try {
            const { data: maintenanceResp }: any = await fetchPathPositionCoords({ roadId, pathId, position: pathPositionQuery })

            if (
                Object.hasOwn(maintenanceResp, "Error") &&
                (maintenanceResp.Error === "Invalid maintenance position" || maintenanceResp.Error === "Coordinates are inexact")
            ) {
                throw new Error("Эксплуатационный километр не найден. Производится поиск по проектному километру.")
            }

            coordsResponse = maintenanceResp
        } catch (error: any) {
            dispatch(showToastMessage({ messageText: error.message, messageType: "error", windowType: "map" }))

            const projectPosition = pathPositionQuery.replace("+", ".")
            const { data: projectResp }: any = await fetchPathPositionCoords({ roadId, pathId, position: projectPosition })

            if (Object.hasOwn(projectResp, "Error")) {
                let messageText = "Выбранная дорогая не проходит через указанное положение."
                if (selectedPathMinPosition && selectedPathMaxEnding) {
                    messageText = `Выбранная дорогая не проходит через указанное положение. Выберите положение в приблизительном промежутке  от ${selectedPathMinPosition} до ${selectedPathMaxEnding}.`
                }
                throw new Error(messageText)
            }

            coordsResponse = projectResp
        }

        const { Latitude, Longitude } = coordsResponse ?? {}
        const mapPosition = { latitude: Latitude, longitude: Longitude }

        goToPosition(mapPosition, {
            road: roadName,
            path: pathName,
            kmM: pathPositionQuery,
            title: "Результат поиска"
        })
        
        // Note: MapSearchbar originally did this:
        // road: roadName ?? searchInputValue
        // But logic suggests if we are searching ON selected path, roadName should exist.
        // If not, fall back to searchInputValue which probably holds the road name.
        // I'll stick to logic:
        // if (!roadName) setSearchInputValue(displayName) might be called before.
        // But `goToPosition` uses `overlayMeta`.
    }

    function normalizePathPositionInput(value: any) {
        const normalized = normalizePathPositionUtils(value)
        if (normalized) {
            setPathPositionInputValue(normalized)
            setPathPositionQuery(normalized)
        } else {
            dispatch(showToastMessage({ messageText: "Значение Км+ должно соответствовать формату км+м", messageType: "error", windowType: "map" }))
        }
    }

    return {
        isPathSelected,
        setIsPathSelected,
        selectedPathMeta,
        setSelectedPathMeta,
        pathPositionInputValue,
        setPathPositionInputValue,
        pathPositionQuery,
        setPathPositionQuery,
        selectedPathMinPosition,
        selectedPathMaxEnding,
        isFetchingWfs,
        isFetchingPathPos,
        
        highlightPathById,
        goToPath,
        goToPosition,
        goToSearchResult,
        goToPositionOnSelectedPath,
        normalizePathPositionInput
    }
}

